import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

const ServiceContactForm = ({ serviceName }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: serviceName || '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', { ...formData, service: serviceName });
    toast.success('Thank you! We\'ll get back to you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      company: '',
      service: serviceName || '',
      message: ''
    });
  };

  return (
    <section id="contact-form" className="py-24 px-4 lg:px-8 bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a] text-white">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
            Ready to
            <br />
            <span className="bg-gradient-to-r from-light-pink to-light-blue bg-clip-text text-transparent">
              Get Started?
            </span>
          </h2>
          <p className="text-xl text-white/80">
            Fill out the form below and we'll get back to you within 24 hours
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="space-y-6 bg-white/10 backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-white/20"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold uppercase tracking-wider mb-2 text-white">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-4 bg-white/20 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-light-pink focus:border-transparent transition-all text-white placeholder-white/60 font-medium"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold uppercase tracking-wider mb-2 text-white">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-4 bg-white/20 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-light-pink focus:border-transparent transition-all text-white placeholder-white/60 font-medium"
                placeholder="john@company.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-semibold uppercase tracking-wider mb-2 text-white">
              Company Name
            </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-4 bg-white/20 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-light-pink focus:border-transparent transition-all text-white placeholder-white/60 font-medium"
              placeholder="Your Company"
            />
          </div>

          <div>
            <label htmlFor="service" className="block text-sm font-semibold uppercase tracking-wider mb-2 text-white">
              Service Interested In *
            </label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
              className="w-full px-4 py-4 bg-white/20 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-light-pink focus:border-transparent transition-all text-white font-medium"
            >
              <option value="" className="bg-[#1a0a2e] text-white">Select a service</option>
              <option value="Web Development" className="bg-[#1a0a2e] text-white">Web Development</option>
              <option value="Mobile App Development" className="bg-[#1a0a2e] text-white">Mobile App Development</option>
              <option value="UI/UX Design" className="bg-[#1a0a2e] text-white">UI/UX Design</option>
              <option value="Digital Marketing" className="bg-[#1a0a2e] text-white">Digital Marketing</option>
              <option value="E-Commerce Development" className="bg-[#1a0a2e] text-white">E-Commerce Development</option>
              <option value="AI & ML Solutions" className="bg-[#1a0a2e] text-white">AI & ML Solutions</option>
              <option value="Data Analytics" className="bg-[#1a0a2e] text-white">Data Analytics</option>
              <option value="Cloud Solutions" className="bg-[#1a0a2e] text-white">Cloud Solutions</option>
              <option value="Blockchain Development" className="bg-[#1a0a2e] text-white">Blockchain Development</option>
              <option value="Creative Copywriting" className="bg-[#1a0a2e] text-white">Creative Copywriting</option>
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold uppercase tracking-wider mb-2 text-white">
              Project Details *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              className="w-full px-4 py-4 bg-white/20 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-light-pink focus:border-transparent transition-all resize-none text-white placeholder-white/60 font-medium"
              placeholder="Tell us about your project..."
            />
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-5 rounded-xl bg-gradient-to-r from-light-pink to-mid-purple text-white font-bold text-base uppercase tracking-wider hover:shadow-2xl transition-all flex items-center justify-center gap-3 group"
          >
            Send Message
            <Send size={20} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.form>
      </div>
    </section>
  );
};

export default ServiceContactForm;
